import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { DownloadInfo, PreregPublic, ServerStatusResponse } from '@shared/types';
import { api } from '@/lib/api';
import { bytes } from '@/lib/format';
import { useTitle } from '@/lib/hooks';
import { useConfig, useUser } from '@/lib/session';
import { Countdown } from '@/components/bits';
import {
  IconAndroid,
  IconApple,
  IconArrowRight,
  IconCastle,
  IconClock,
  IconCoins,
  IconHandshake,
  IconPlay,
  IconSkull,
  IconSword,
  IconUsers,
} from '@/components/icons';
import { Badge, Container, Dot, ExternalButton, LinkButton, Panel, SectionTitle } from '@/components/ui';
import { FeatureShowcase } from '@/components/FeatureShowcase';
import { SocialButtons, useSocials } from '@/components/Social';
import { SHOTS } from '@/content/shots';
import { WorldNow } from './home/WorldNow';

function useStatus() {
  return useQuery({ queryKey: ['status'], queryFn: () => api<ServerStatusResponse>('/status'), refetchInterval: 15_000 });
}

function LiveStrip() {
  const { data } = useStatus();
  const up = !!data && data.login && data.char && data.map;
  const woeLive = data?.woe.active[0];
  return (
    <div className="grid grid-cols-3 divide-x divide-line border border-line bg-scrim/70 backdrop-blur-md">
      <div className="px-3 py-3 sm:px-5">
        <p className="text-[0.66rem] font-semibold uppercase tracking-[0.16em] text-dim">Server</p>
        <p className="mt-1 flex items-center gap-2 font-semibold">
          <Dot on={up} />
          {data ? (up ? 'Online' : 'Offline') : '...'}
        </p>
      </div>
      <div className="px-3 py-3 sm:px-5">
        <p className="text-[0.66rem] font-semibold uppercase tracking-[0.16em] text-dim">Online now</p>
        {/* Everyone in the world: players, staff, auto-farmers and vendors. */}
        <p className="mt-1 font-semibold text-gold-bright">{data ? (data.map ? data.inWorld : 0).toLocaleString() : '...'}</p>
      </div>
      <div className="px-3 py-3 sm:px-5">
        <p className="text-[0.66rem] font-semibold uppercase tracking-[0.16em] text-dim">{woeLive ? 'WoE is live' : 'Next siege'}</p>
        <p className="mt-1 font-semibold">
          {woeLive ? (
            <span className="text-danger">
              ends in <Countdown to={woeLive.endsAt} />
            </span>
          ) : data?.woe.next ? (
            <Countdown to={data.woe.next.startsAt} />
          ) : (
            '...'
          )}
        </p>
      </div>
    </div>
  );
}

/** Hero badge while pre-registration runs: the live count, linking to /pre-register. */
function PreregBadge() {
  const { data } = useQuery({ queryKey: ['prereg'], queryFn: () => api<PreregPublic>('/prereg'), staleTime: 60_000 });
  return (
    <Link to="/pre-register" className="group">
      <Badge tone="gold" className="bg-scrim/75 backdrop-blur-sm group-hover:border-gold">
        Pre-registration open{data ? ` · ${data.count.toLocaleString()} counted` : ''}
        <IconArrowRight size={13} />
      </Badge>
    </Link>
  );
}

function Hero() {
  const config = useConfig();
  const user = useUser();
  const download = useQuery({ queryKey: ['download'], queryFn: () => api<DownloadInfo>('/download'), staleTime: 5 * 60_000 });
  const [params] = useSearchParams();
  const ref = params.get('ref');
  const android = download.data?.android;
  const ios = config.iosTestflightUrl;
  return (
    <section className="relative isolate overflow-hidden">
      <picture>
        <source media="(max-width: 960px)" srcSet="/img/hero-960.webp" />
        {/* Phones: the art is a 540px band sized so the whole logo fits, and the
            text starts on its faded lower edge. Wider screens: full-bleed. */}
        <img
          src="/img/hero.webp"
          alt="An adventurer and a pink blob look out over a floating castle city at sunset"
          className="absolute inset-x-0 top-0 -z-10 h-[540px] w-full object-cover object-[50%_18%] md:inset-y-0 md:h-full"
          fetchPriority="high"
        />
      </picture>
      <div className="absolute inset-x-0 top-0 -z-10 h-[540px] bg-gradient-to-b from-transparent from-35% to-ground md:hidden" />
      <div className="absolute inset-0 -z-10 hidden bg-gradient-to-t from-ground via-ground/55 to-ground/0 md:block" />
      <div className="absolute inset-0 -z-10 hidden bg-gradient-to-r from-ground/80 via-ground/10 to-transparent md:block" />
      <Container wide className="flex flex-col justify-end pb-10 pt-[260px] sm:pb-14 md:min-h-[max(560px,min(92svh,860px))] md:pt-56">
        <div className="max-w-2xl animate-rise">
          <div className="mb-4 flex flex-wrap gap-2 [&>span]:bg-scrim/75 [&>span]:backdrop-blur-sm">
            {config.preregOpen ? <PreregBadge /> : null}
            {config.testPhase ? <Badge tone="gold">{config.testPhase}</Badge> : null}
            <Badge className="text-ink">{config.rateLabel} · pre-renewal</Badge>
            {ref ? <Badge tone="ok">Invite code {ref.toUpperCase()} applied</Badge> : null}
          </div>
          <h1 className="max-w-xl font-display text-4xl leading-[1.08] tracking-wide text-ink drop-shadow-[0_2px_18px_rgb(0_0_0/0.7)] sm:text-5xl lg:text-[3.35rem]">
            {config.tagline}
          </h1>
          <p className="mt-4 max-w-xl text-lg leading-relaxed text-ink/85 drop-shadow-[0_1px_8px_rgb(0_0_0/0.8)]">
            Classic jobs, guild wars and MVP hunts, rebuilt for your phone. Free to play, with a fair cash shop.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            {android ? (
              <ExternalButton href={android.url} download size="lg">
                <IconAndroid size={20} />
                Download for Android
              </ExternalButton>
            ) : (
              <LinkButton to="/download" size="lg">
                <IconAndroid size={20} />
                Download for Android
              </LinkButton>
            )}
            {ios ? (
              <ExternalButton href={ios} size="lg">
                <IconApple size={20} />
                Get it on iPhone
              </ExternalButton>
            ) : null}
            {user ? (
              <LinkButton to="/donate" variant="ghost" size="lg">
                Buy donation points
              </LinkButton>
            ) : (
              <LinkButton
                to={config.preregOpen ? (ref ? `/pre-register?ref=${encodeURIComponent(ref)}` : '/pre-register') : ref ? `/register?ref=${encodeURIComponent(ref)}` : '/register'}
                variant="ghost"
                size="lg"
              >
                {config.preregOpen ? 'Pre-register free' : 'Create free account'}
              </LinkButton>
            )}
          </div>
          <p className="mt-3 text-sm text-dim">
            {android ? `Version ${android.version} · ${bytes(android.sizeBytes)} · ` : ''}
            <Link to="/download" className="underline decoration-line-strong underline-offset-4 hover:text-ink">
              {ios ? 'Install help · iPhone is a TestFlight beta, limited slots' : 'Install help · iOS coming soon'}
            </Link>
          </p>
          <SocialButtons className="mt-6 flex-col sm:flex-row" />
        </div>
        <div className="mt-10 max-w-2xl">
          <LiveStrip />
        </div>
      </Container>
    </section>
  );
}

const FEATURES = [
  { icon: IconSword, title: 'Classic, pre-renewal rules', body: 'Swordman to Lord Knight, Acolyte to High Priest. The stats, skills and level caps you remember.' },
  { icon: IconClock, title: 'Auto-farm built in', body: 'The in-game hunting assistant keeps working while you rest: free time every day, more with VIP.' },
  { icon: IconSkull, title: 'MVP hunts', body: 'Boss timers on the in-game MVP board and live on this site, with who took the last kill.' },
  { icon: IconCastle, title: 'Guilds and War of Emperium', body: 'Twenty classic castles plus the second-edition sieges, three nights every week.' },
  { icon: IconCoins, title: 'A fair cash shop', body: 'Boosts, rentals and costumes, priced in US dollars for players across Southeast Asia.' },
  { icon: IconAndroid, title: 'Made for phones', body: 'Android and iPhone. Touch controls and an interface designed for small screens, with content that updates in the background.' },
];

function Features() {
  return (
    <section className="py-20">
      <SectionTitle eyebrow="Why players stay" title="Everything you loved, in your pocket" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {FEATURES.map(({ icon: Icon, title, body }) => (
          <Panel key={title} inner="p-6 h-full">
            <Icon size={26} className="text-gold" />
            <h3 className="mt-4 font-display text-xl tracking-wide text-ink">{title}</h3>
            <p className="mt-2 leading-relaxed text-muted">{body}</p>
          </Panel>
        ))}
      </div>
    </section>
  );
}

function Intro() {
  const [playing, setPlaying] = useState(false);
  return (
    <section className="pb-20">
      <SectionTitle eyebrow="Intro" title="See it in motion" />
      <div className="cut-lg bg-line p-px">
        <div className="cut-inner relative aspect-video overflow-hidden bg-scrim">
          {playing ? (
            <video src="/media/intro.mp4" className="h-full w-full" controls autoPlay playsInline />
          ) : (
            <button type="button" onClick={() => setPlaying(true)} className="group absolute inset-0" aria-label="Play the intro video">
              <img
                src="/img/hero-alt-960.webp"
                srcSet="/img/hero-alt-960.webp 960w, /img/hero-alt.webp 1670w"
                sizes="(min-width: 1024px) 60vw, 100vw"
                alt=""
                loading="lazy"
                className="h-full w-full object-cover opacity-80 transition-opacity group-hover:opacity-100"
              />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="flex h-20 w-20 items-center justify-center rounded-full border border-gold/70 bg-scrim/70 text-gold-bright backdrop-blur transition-transform group-hover:scale-110">
                  <IconPlay size={32} />
                </span>
              </span>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

// (The War of Emperium countdown lives in WorldNow and the hero's live strip.)
function ServerInfo() {
  const config = useConfig();
  return (
    <section className="grid gap-4 pb-20 lg:grid-cols-2">
      <Panel inner="p-6 h-full">
        <p className="eyebrow">&#9670; Rates</p>
        <p className="mt-3 font-display text-2xl tracking-wide text-ink">{config.rateLabel} server</p>
        {config.testPhase ? (
          <p className="mt-1 text-sm text-muted">
            <span className="font-semibold text-gold-bright">Temporary</span> test rates for the {config.testPhase.toLowerCase()}:
          </p>
        ) : null}
        <div className="mt-4 grid grid-cols-3 gap-3 text-center">
          {[
            ['Base', config.rates.base],
            ['Job', config.rates.job],
            ['Drop', config.rates.drop],
          ].map(([k, v]) => (
            <div key={k} className="border border-line bg-ground/60 py-4">
              <p className="text-3xl font-semibold text-gold-bright">x{v}</p>
              <p className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-dim">{k}</p>
            </div>
          ))}
        </div>
        {config.ratesNote ? <p className="mt-4 text-sm text-dim">{config.ratesNote}</p> : null}
      </Panel>
      <Panel inner="p-6 h-full" tone="gold">
        <p className="eyebrow">&#9670; Support the game</p>
        <p className="mt-4 font-display text-2xl tracking-wide">Donation points</p>
        <p className="mt-2 leading-relaxed text-muted">Pay with USDT (TRC20) here, or through an official reseller with your local e-wallet or bank.</p>
        <div className="mt-5 flex flex-wrap gap-3">
          <LinkButton to="/donate" size="sm">
            Buy with USDT
          </LinkButton>
          <LinkButton to="/resellers" variant="ghost" size="sm">
            Find a reseller
          </LinkButton>
        </div>
      </Panel>
    </section>
  );
}

function Community() {
  const socials = useSocials();
  return (
    <section className="grid gap-4 md:grid-cols-2">
      <Panel inner="p-7 h-full" size="lg">
        <IconUsers size={28} className="text-gold" />
        <h3 className="mt-4 font-display text-2xl tracking-wide">Join the community</h3>
        <p className="mt-2 leading-relaxed text-muted">Find a party, trade gear, report bugs and hear about events first.</p>
        <div className="mt-6">
          {socials.length ? (
            <SocialButtons />
          ) : (
            <LinkButton to="/news" variant="ghost">
              Read the news
            </LinkButton>
          )}
        </div>
      </Panel>
      <Panel inner="p-7 h-full" size="lg">
        <IconHandshake size={28} className="text-gold" />
        <h3 className="mt-4 font-display text-2xl tracking-wide">Stream it or sell it</h3>
        <p className="mt-2 leading-relaxed text-muted">
          We are looking for streamers: stream Project R 1 to 2 times a week and let your viewers back you with the support points they earn on every top-up,
          yours in points or cash. No follower count needed. Resellers buy points at a discount and sell them locally.
        </p>
        <div className="mt-6">
          <LinkButton to="/partners" variant="ghost">
            Become a partner
          </LinkButton>
        </div>
      </Panel>
    </section>
  );
}

export default function Home() {
  useTitle(null);
  return (
    <>
      <Hero />
      <Container wide>
        <WorldNow />
        <Features />
        <FeatureShowcase shots={SHOTS} eyebrow="A look inside" title="Features up close" />
        <Intro />
        <ServerInfo />
        <Community />
      </Container>
    </>
  );
}
